import { Button, Caption } from '@farol-ds/react'
import { withTopbarProps } from '@jusbrasil/topbar'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { externalAuthApi, internalAuthApi } from '../../../api'
import { AuthLayout, PasswordResetSuccess } from '../../../components'
import { LoginCard } from '../../../components/login-card/login-card'
import { PasswordField } from '../../../components/password-field/password-field'
import { JUSID_PLATFORM_WEB_AUTH, PASSWORD_RESET_EVENTS } from '../../../constants'
import { getAuthServerUrl } from '../../../experiments'
import { usePasswordRecovery } from '../../../hooks/usePasswordRecovery'
import { ERROR_MESSAGES } from '../../../utils/errorMessages'
import { parseErrorMessage } from '../../../utils/parseErrorMessage'
import { MIN_SIZE, validatePasswordRules } from '../../../utils/passwordStrength'
import styles from './token.module.scss'

function getPasswordFieldError(
  touched: boolean,
  value: string,
  ruleError: string | null
): string | undefined {
  const trimmedValue = value.trim()
  if (touched && !trimmedValue) return ERROR_MESSAGES.passwordRecovery.reset.password.empty
  return ruleError ?? undefined
}

function getConfirmPasswordFieldError(
  touched: boolean,
  confirmValue: string,
  passwordValue: string
): string | undefined {
  const trimmedConfirmValue = confirmValue.trim()
  const trimmedPasswordValue = passwordValue.trim()
  if (touched && !trimmedConfirmValue) {
    return ERROR_MESSAGES.passwordRecovery.reset.confirmPassword.empty
  }
  if (trimmedConfirmValue.length > 0 && trimmedPasswordValue !== trimmedConfirmValue) {
    return ERROR_MESSAGES.passwordRecovery.reset.confirmPassword.mismatch
  }
  return undefined
}

export interface PasswordRecoveryTokenPageProps {
  token?: string
  authCsrfToken?: string | null
}

export function PasswordRecoveryTokenPage({ token: tokenProp }: PasswordRecoveryTokenPageProps) {
  const router = useRouter()
  const token = tokenProp || (router.query.token as string)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [touched, setTouched] = useState<{ password?: boolean; confirmPassword?: boolean }>({})
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false)
  const [passwordRuleError, setPasswordRuleError] = useState<string | null>(null)
  const { isLoading, error, handleSetNewPasswordFromRecoveryToken } = usePasswordRecovery()

  useEffect(() => {
    sendMetrics(PASSWORD_RESET_EVENTS.SCREEN_SEEN, null, {
      jusid_platform: JUSID_PLATFORM_WEB_AUTH,
    })
  }, [])

  const [localError, setLocalError] = useState<string | null>(null)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ password: true, confirmPassword: true })
    setLocalError(null)
    setPasswordRuleError(null)
    const passwordValidation = validatePasswordRules(password, MIN_SIZE)
    if (!passwordValidation.isValid && passwordValidation.errorMessage) {
      setPasswordRuleError(passwordValidation.errorMessage)
      return
    }
    if (password !== confirmPassword) {
      setLocalError(ERROR_MESSAGES.passwordRecovery.reset.confirmPassword.mismatch)
      return
    }
    const success = await handleSetNewPasswordFromRecoveryToken(token, password)
    if (success) {
      sendMetrics(PASSWORD_RESET_EVENTS.SUCCESSFUL, null, {
        jusid_platform: JUSID_PLATFORM_WEB_AUTH,
      })
      setPasswordResetSuccess(true)
    }
  }

  const displayError = localError || error
  const parsedError = displayError ? parseErrorMessage(displayError) : null
  const header = {
    title: 'Redefinir senha',
    ...(parsedError && {
      errorMessage: {
        title: parsedError.title,
        description: parsedError.description,
      },
    }),
  }

  if (passwordResetSuccess) {
    return (
      <AuthLayout>
        <PasswordResetSuccess />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <LoginCard header={header} hideCloseButton>
        <form onSubmit={handleSubmit} noValidate>
          <PasswordField
            value={password}
            onChange={(value) => {
              setPassword(value)
              const validation = validatePasswordRules(value, MIN_SIZE)
              setPasswordRuleError(
                !validation.isValid && validation.errorMessage ? validation.errorMessage : null
              )
            }}
            error={getPasswordFieldError(!!touched.password, password, passwordRuleError)}
          />
          <div className={styles.spacer} />
          <PasswordField
            value={confirmPassword}
            onChange={setConfirmPassword}
            error={getConfirmPasswordFieldError(
              !!touched.confirmPassword,
              confirmPassword,
              password
            )}
            hideRequirements
            label="Confirmar senha"
            hideStrengthIndicator
          />
          <Button type="submit" kind="primary" loading={isLoading} className={styles.submitButton}>
            Redefinir senha
          </Button>
          <Caption className={styles.captionSessions}>
            Isso encerrará todas as suas sessões ativas.
          </Caption>
        </form>
      </LoginCard>
    </AuthLayout>
  )
}

const getServerSideProps: GetServerSideProps = async (ctx) => {
  const token = ctx.params?.token as string | undefined
  if (!token) {
    return {
      notFound: true,
    }
  }

  const baseUrl = getAuthServerUrl()

  const result = await externalAuthApi.verifyPasswordRecoveryToken(token, ctx)
  if (!result || !result.response.ok) {
    return {
      notFound: true,
    }
  }

  const { csrfToken } = await internalAuthApi.getCsrfToken(ctx, { baseUrl })
  const tokenProp = baseUrl ? { authCsrfToken: csrfToken } : { csrfToken }
  return {
    props: {
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      token,
      ...tokenProp,
    },
  }
}

export const PasswordRecoveryTokenPageExports = {
  getServerSideProps,
}
